import { useSiteContext, useTranslation } from '@eti/providers';
import { useState, useEffect } from 'react';
import { constants } from '@eti/utils';
import { css } from '@eti/styles';
import { Heading, vars, Stack, Text, textVariants, InfoButton } from '@etg/wings';
import { Tier } from '@eti/schema-types';
import { BUNDLE_PRODUCT } from '../../utils/constants';
import { useStylesVariables } from '../../utils/utils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  FLIGHTNETWORK,
  GOTOGATE,
  MYTRIP
} = constants.brands;
const wrapperStyles = css`
  display: flex;
  flex-direction: row;
`;
const illustrationWrapperStyles = firstColumnWidth => css`
  display: flex;
  justify-content: center;
  width: ${firstColumnWidth};
`;
const getIllustrationStyles = illustrationUrl => css`
  background: url(${illustrationUrl});
  background-position: bottom;
  background-repeat: no-repeat;
  background-size: contain;
  height: calc(100% - 8px);
  width: calc(100% - 16px);

  [dir='rtl'] & {
    transform: rotateY(180deg);
  }
`;
const contentStyles = css`
  display: flex;
  flex: 1;
  flex-direction: column;
`;
const badgesWrapperStyles = css`
  display: flex;
  text-align: center;
`;
const columnHeadersStyles = css`
  display: flex;
`;
const recommendedBadgeStyles = css`
  background-color: ${vars.colors.promotional.foreground};
  border-radius: 3px 3px 0 0;
  color: ${vars.colors.promotional.background};
  display: flex;
  flex: 1;
  justify-content: center;
  margin-inline-end: -1px;
  padding: 4px 0;
`;
const optionTitleStyles = (hasBadgeOnTop, recommendedBackground, rowsHeight) => css`
  align-items: center;
  background-color: ${hasBadgeOnTop && recommendedBackground};
  border-inline-end: 1px solid ${vars.colors.divider};
  border-top: 1px solid ${vars.colors.divider};
  cursor: pointer;
  display: flex;
  flex: 1;
  gap: 5px;
  justify-content: center;
  min-height: ${rowsHeight};
  text-align: center;

  span:first-of-type {
    max-width: 140px;
    overflow-wrap: break-word;
  }

  &:first-child {
    border-inline-start: 1px solid ${vars.colors.divider};
    border-start-start-radius: ${!hasBadgeOnTop && '4px'};
    margin-inline-start: 0.02rem;
  }

  &:last-child {
    border-start-end-radius: ${!hasBadgeOnTop && '4px'};
  }
`;
const useGetIllustrationUrl = () => {
  const {
    brand: {
      code: brand
    }
  } = useSiteContext();
  const [illustrationUrl, setIllustrationUrl] = useState('');
  useEffect(() => {
    const fetchIllustration = async () => {
      const brandFolder = brand === FLIGHTNETWORK || brand === GOTOGATE || brand === MYTRIP ? brand : 'default';
      try {
        const src = await import(`@eti/assets/illustrations/${brandFolder}/ancillary-bundle.svg?url`);
        return setIllustrationUrl(src.default);
      } catch {
        return null;
      }
    };
    fetchIllustration();
  }, [brand]);
  return illustrationUrl;
};
const BundleHeader = ({
  bundleTitle,
  discountPercentageTitle,
  handleModalOpen,
  isOpen,
  recommendedText,
  sellableOptions
}) => {
  const illustrationUrl = useGetIllustrationUrl();
  const {
    firstColumnWidth,
    recommendedBackground,
    rowsHeight
  } = useStylesVariables();
  const {
    t
  } = useTranslation();
  return /*#__PURE__*/_jsxs("div", {
    className: wrapperStyles,
    "data-testid": `${BUNDLE_PRODUCT}-header`,
    children: [/*#__PURE__*/_jsx("div", {
      className: illustrationWrapperStyles(firstColumnWidth),
      children: /*#__PURE__*/_jsx("div", {
        className: getIllustrationStyles(illustrationUrl)
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: contentStyles,
      children: [/*#__PURE__*/_jsxs(Stack, {
        spacing: 16,
        children: [/*#__PURE__*/_jsx(Heading, {
          level: 2,
          children: bundleTitle
        }), /*#__PURE__*/_jsx(Text, {
          children: discountPercentageTitle
        }), /*#__PURE__*/_jsx("div", {
          className: badgesWrapperStyles,
          children: sellableOptions.map(({
            id,
            type
          }) => /*#__PURE__*/_jsx("div", {
            style: {
              flex: 1
            },
            children: type === Tier.Recommended && /*#__PURE__*/_jsx(Text, {
              className: recommendedBadgeStyles,
              variant: textVariants._14_BOLD,
              children: recommendedText
            })
          }, id))
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: columnHeadersStyles,
        children: sellableOptions.map(({
          id,
          title,
          type
        }) => /*#__PURE__*/_jsxs("div", {
          className: optionTitleStyles(type === Tier.Recommended, recommendedBackground, rowsHeight),
          children: [/*#__PURE__*/_jsx(Text, {
            variant: textVariants._18_EMPHASIZED,
            children: title
          }), /*#__PURE__*/_jsx(InfoButton, {
            "aria-controls": "product-bundle-info-drawer",
            "aria-expanded": isOpen ? 'true' : 'false',
            "aria-haspopup": "dialog",
            "data-testid": `${BUNDLE_PRODUCT}-title-info-icon-${id}`,
            id: `${BUNDLE_PRODUCT}.${id}`,
            label: `${title}. ${t('ScreenReader.Product.Bundle.InfoButton.Mobile.Text')}`,
            onClick: () => handleModalOpen(id)
          })]
        }, id))
      })]
    })]
  });
};
export default BundleHeader;
//# sourceMappingURL=BundleHeader.js.map